import React, { useState, useEffect, useMemo, useCallback } from "react";
import io from "socket.io-client";
import "./App.css";

// --- Configuration ---
const API_BASE_URL = "https://cryptotrendingbackend.luknos.com/api/v1";
const SOCKET_URL = "https://cryptotrendingbackend.luknos.com/";

// --- API Service ---
const apiService = {
  fetchGlobalSettings: () =>
    fetch(`${API_BASE_URL}/settings/global?timestamp=${Date.now()}`).then(
      (res) => res.json()
    ),
  fetchCoinConfigs: () =>
    fetch(`${API_BASE_URL}/settings/coins?timestamp=${Date.now()}`).then(
      (res) => res.json()
    ),
  fetchTradeHistory: (filters) => {
    const params = new URLSearchParams(filters);
    params.append("timestamp", Date.now());
    return fetch(`${API_BASE_URL}/trades?${params.toString()}`).then((res) =>
      res.json()
    );
  },
  fetchOpenTrades: (coin) =>
    fetch(
      `${API_BASE_URL}/trades/open?coin=${coin}&timestamp=${Date.now()}`
    ).then((res) => res.json()),
  fetchInstruments: () =>
    fetch(`${API_BASE_URL}/coins/instruments`).then((res) => res.json()),
  addCoin: (coin_pair) =>
    fetch(`${API_BASE_URL}/coins/add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ coin_pair }),
    }).then((res) => res.json()),
  updateCoinSettings: (settings) =>
    fetch(`${API_BASE_URL}/coins/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    }).then((res) => res.json()),
  deactivateCoin: (coinPair) =>
    fetch(`${API_BASE_URL}/coins/deactivate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ coin_pair: coinPair }),
    }).then((res) => res.json()),
  panicSettle: (coin_pair) =>
    fetch(`${API_BASE_URL}/bot/panic`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ coin_pair }),
    }).then((res) => res.json()),
};

// --- Reusable UI Components ---
const InfoCard = ({ title, value, subValue, valueColor, statusLight }) => (
  <div className="info-card">
    <h3 className="info-card-title">
      {statusLight && <span className={`status-dot ${statusLight}`}></span>}
      {title}
    </h3>
    <p className={`info-card-value ${valueColor || ""}`}>{value}</p>
    {subValue && <p className="info-card-sub-value">{subValue}</p>}
  </div>
);

const ConfirmationModal = ({ message, onConfirm, onCancel }) => (
  <div className="modal-overlay">
    <div className="modal-content">
      <p className="modal-message">{message}</p>
      <div className="modal-actions">
        <button onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button onClick={onConfirm} className="btn btn-panic">
          Confirm
        </button>
      </div>
    </div>
  </div>
);

// --- Main App Component ---
export default function App() {
  const [view, setView] = useState("dashboard");
  const [globalSettings, setGlobalSettings] = useState(null);
  const [coinConfigs, setCoinConfigs] = useState([]);
  const [refetchCounter, setRefetchCounter] = useState(0);
  const [activeCoin, setActiveCoin] = useState(null);
  const [activeCoinData, setActiveCoinData] = useState({
    openTrade: null,
    history: [],
    totalPnl: 0,
  });
  const [livePrices, setLivePrices] = useState({});
  const [uiState, setUiState] = useState({
    loading: true,
    error: null,
    isAddCoinModalOpen: false,
    modalConfig: null,
  });

  const fetchAllData = useCallback(async () => {
    if (!globalSettings) setUiState((prev) => ({ ...prev, loading: true }));
    setUiState((prev) => ({ ...prev, error: null }));
    try {
      const [globalData, coinsData] = await Promise.all([
        apiService.fetchGlobalSettings(),
        apiService.fetchCoinConfigs(),
      ]);
      setGlobalSettings(globalData);
      setCoinConfigs(coinsData);

      const currentActiveCoinStillExists = coinsData.some(
        (c) => c.coin_pair === activeCoin
      );
      if (coinsData.length > 0 && !currentActiveCoinStillExists) {
        setActiveCoin(coinsData[0].coin_pair);
      } else if (coinsData.length === 0) {
        setActiveCoin(null);
      }
    } catch (err) {
      setUiState((prev) => ({ ...prev, error: err.message }));
    } finally {
      setUiState((prev) => ({ ...prev, loading: false }));
    }
  }, [activeCoin, globalSettings]);

  useEffect(() => {
    fetchAllData();
  }, [refetchCounter]);

  useEffect(() => {
    if (!activeCoin) return;
    const fetchCoinSpecificData = async () => {
      try {
        const [openTrades, historyData] = await Promise.all([
          apiService.fetchOpenTrades(activeCoin),
          apiService.fetchTradeHistory({ coin: activeCoin }),
        ]);
        setActiveCoinData({
          openTrade: openTrades[0] || null,
          history: historyData.trades,
          totalPnl: historyData.totalPnl,
        });
      } catch (err) {
        setUiState((prev) => ({
          ...prev,
          error: `Failed to load data for ${activeCoin}: ${err.message}`,
        }));
      }
    };
    fetchCoinSpecificData();
  }, [activeCoin, refetchCounter]);

  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ["polling"] });
    socket.on("connect", () =>
      console.log("✅ Polling connection established.")
    );

    socket.on("live_price_update", ({ coin, price }) => {
      setLivePrices((prev) => ({
        ...prev,
        [coin]: price,
      }));
    });

    const triggerRefetch = () => {
      setTimeout(() => setRefetchCounter((c) => c + 1), 500);
    };

    socket.on("new_trade", triggerRefetch);
    socket.on("trade_closed", triggerRefetch);
    socket.on("panic_success", triggerRefetch);

    return () => {
      socket.off("new_trade", triggerRefetch);
      socket.off("trade_closed", triggerRefetch);
      socket.off("panic_success", triggerRefetch);
      socket.disconnect();
    };
  }, []);

  const currentCoinConfig = useMemo(
    () => coinConfigs.find((c) => c.coin_pair === activeCoin),
    [coinConfigs, activeCoin]
  );

  const handleUpdateSingleCoin = (updatedCoin) => {
    setCoinConfigs((currentConfigs) =>
      currentConfigs.map((c) => (c.id === updatedCoin.id ? updatedCoin : c))
    );
  };

  if (uiState.loading)
    return <div className="loading-container">Loading Dashboard...</div>;
  if (uiState.error)
    return <div className="error-container">Error: {uiState.error}</div>;

  return (
    <div className="app-container">
      {uiState.modalConfig && <ConfirmationModal {...uiState.modalConfig} />}
      <TabNavigation
        coins={coinConfigs}
        activeCoin={activeCoin}
        setActiveCoin={setActiveCoin}
        onAddCoin={() =>
          setUiState((prev) => ({ ...prev, isAddCoinModalOpen: true }))
        }
        onSwitchView={() =>
          setView((v) => (v === "dashboard" ? "summary" : "dashboard"))
        }
        currentView={view}
        onUpdate={fetchAllData}
        setModal={(config) =>
          setUiState((prev) => ({ ...prev, modalConfig: config }))
        }
      />
      {uiState.isAddCoinModalOpen && (
        <AddCoinModal
          onClose={() =>
            setUiState((prev) => ({ ...prev, isAddCoinModalOpen: false }))
          }
          onCoinAdded={fetchAllData}
          existingCoins={coinConfigs.map((c) => c.coin_pair)}
        />
      )}

      {view === "dashboard" && activeCoin && currentCoinConfig ? (
        <CoinDashboard
          key={activeCoin}
          coinConfig={currentCoinConfig}
          globalSettings={globalSettings}
          coinData={activeCoinData}
          livePrice={livePrices[activeCoin] || 0}
          onSettingsChange={handleUpdateSingleCoin}
          setModal={(config) =>
            setUiState((prev) => ({ ...prev, modalConfig: config }))
          }
        />
      ) : view === "summary" ? (
        <TradeSummaryPage />
      ) : (
        <div className="card">
          <h2>No coins are being tracked. Click "+ Add" to begin.</h2>
        </div>
      )}
    </div>
  );
}

function TabNavigation({
  coins,
  activeCoin,
  setActiveCoin,
  onAddCoin,
  onSwitchView,
  currentView,
  onUpdate,
  setModal,
}) {
  const handleDelete = (e, coinPair) => {
    e.stopPropagation();
    setModal({
      message: `This will settle any open trades for ${coinPair} and remove the tab. Are you sure?`,
      onConfirm: async () => {
        try {
          await apiService.deactivateCoin(coinPair);
          onUpdate();
        } catch (error) {
          alert(`Failed to remove tab: ${error.message}`);
        }
        setModal(null);
      },
      onCancel: () => setModal(null),
    });
  };

  return (
    <header className="app-header">
      <nav className="tab-nav">
        {coins.map((coin) => (
          <button
            key={coin.coin_pair}
            onClick={() => setActiveCoin(coin.coin_pair)}
            className={`tab-button ${
              activeCoin === coin.coin_pair ? "active" : ""
            }`}
          >
            {coin.coin_pair}
            <span
              onClick={(e) => handleDelete(e, coin.coin_pair)}
              className="delete-tab-btn"
            >
              ×
            </span>
          </button>
        ))}
        <button onClick={onAddCoin} className="tab-button add-new">
          +
        </button>
      </nav>
      <div className="header-actions">
        <button onClick={onSwitchView} className="view-switch-btn">
          {currentView === "dashboard"
            ? "View Global Summary"
            : "View Dashboard"}
        </button>
      </div>
    </header>
  );
}

function CoinDashboard({
  coinConfig,
  globalSettings,
  coinData,
  livePrice,
  onSettingsChange,
  setModal,
}) {
  const [localConfig, setLocalConfig] = useState(coinConfig);
  const [isBotRunning, setIsBotRunning] = useState(
    localConfig.bot_status === "running"
  );

  const [history, setHistory] = useState(coinData.history);
  const [pnl, setPnl] = useState(coinData.totalPnl);
  const [filters, setFilters] = useState({ from: "", to: "" });

  useEffect(() => {
    setLocalConfig(coinConfig);
    setIsBotRunning(coinConfig.bot_status === "running");
  }, [coinConfig]);

  useEffect(() => {
    setHistory(coinData.history);
    setPnl(coinData.totalPnl);
    setFilters({ from: "", to: "" });
  }, [coinData]);

  const handleSettingChange = (key, value) =>
    setLocalConfig((prev) => ({ ...prev, [key]: value }));

  const handleSaveChanges = async () => {
    try {
      const updatedCoinConfig = await apiService.updateCoinSettings(
        localConfig
      );
      onSettingsChange(updatedCoinConfig);
      alert(`${localConfig.coin_pair} settings saved!`);
    } catch (error) {
      alert(
        `Failed to save settings for ${localConfig.coin_pair}: ${error.message}`
      );
    }
  };

  const handlePanic = () => {
    setModal({
      message: `Are you sure you want to panic settle all trades for ${localConfig.coin_pair}?`,
      onConfirm: async () => {
        await apiService.panicSettle(localConfig.coin_pair);
        setModal(null);
      },
      onCancel: () => setModal(null),
    });
  };

  const handleStartStop = () => {
    const newStatus = isBotRunning ? "stopped" : "running";
    const confirmAction = async () => {
      const settingsToUpdate = {
        coin_pair: localConfig.coin_pair,
        bot_status: newStatus,
      };
      try {
        if (newStatus === "stopped") {
          await apiService.panicSettle(localConfig.coin_pair);
        }

        const updatedCoinConfigFromServer = await apiService.updateCoinSettings(
          settingsToUpdate
        );

        onSettingsChange(updatedCoinConfigFromServer);
      } catch (error) {
        console.error(
          `Failed to update status for ${localConfig.coin_pair}:`,
          error
        );
        alert(
          `Error updating status. Check the browser console (F12) for details.`
        );
      }
      setModal(null);
    };

    if (newStatus === "stopped") {
      setModal({
        message: `This will settle any open trades for ${localConfig.coin_pair}. Are you sure you want to stop the bot?`,
        onConfirm: confirmAction,
        onCancel: () => setModal(null),
      });
    } else {
      confirmAction();
    }
  };

  const currentPnl = useMemo(() => {
    if (!coinData.openTrade || !livePrice) return { value: 0, percent: 0 };
    const trade = coinData.openTrade;
    const value =
      (trade.direction === "long"
        ? livePrice - trade.entry_price
        : trade.entry_price - livePrice) * trade.quantity;

    const percent =
      trade.entry_price > 0 && trade.quantity > 0
        ? (value / (trade.entry_price * trade.quantity)) * 100
        : 0;

    return { value, percent };
  }, [coinData.openTrade, livePrice]);

  const formatTimestamp = (ts) => {
    if (!ts) {
      return "N/A";
    }
    const dateObj = new Date(ts);
    if (isNaN(dateObj)) {
      return "Invalid Date";
    }
    return dateObj.toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  const tradeAmount = useMemo(() => {
    const balance =
      globalSettings.trade_mode === "paper"
        ? globalSettings.paper_wallet_balance
        : globalSettings.live_allocated_capital;
    return ((balance * (localConfig.position_size_percent || 0)) / 100).toFixed(
      2
    );
  }, [globalSettings, localConfig.position_size_percent]);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleApplyFilters = async () => {
    if (!filters.from || !filters.to) {
      alert("Please select both a 'From' and 'To' date and time.");
      return;
    }
    try {
      const result = await apiService.fetchTradeHistory({
        coin: localConfig.coin_pair,
        from: filters.from,
        to: filters.to,
      });
      setHistory(result.trades);
      setPnl(result.totalPnl);
    } catch (error) {
      alert(`Failed to fetch filtered history: ${error.message}`);
    }
  };

  const handleClearFilters = () => {
    setFilters({ from: "", to: "" });
    setHistory(coinData.history);
    setPnl(coinData.totalPnl);
  };

  return (
    <div className="main-grid">
      <div className="trading-column">
        <div className="card">
          <h2 className="card-title">
            Trading Overview: {localConfig.coin_pair}
          </h2>
          <div className="info-card-grid">
            <InfoCard
              title="Live Price"
              value={`$${parseFloat(livePrice || 0).toFixed(5)}`}
              statusLight={livePrice > 0 ? "online" : "offline"}
            />
            <InfoCard
              title="Current Trade Live P/L"
              value={`$${currentPnl.value.toFixed(2)}`}
              subValue={`${currentPnl.percent.toFixed(2)}%`}
              valueColor={currentPnl.value >= 0 ? "text-green" : "text-red"}
            />
          </div>
        </div>
        <div className="card">
          <h2 className="card-title">Current Open Trade</h2>
          {coinData.openTrade ? (
            <div className="current-trade-details">
              <InfoCard
                title="Pair & Direction"
                value={`${
                  coinData.openTrade.coin_pair
                } (${coinData.openTrade.direction.toUpperCase()})`}
                valueColor={
                  coinData.openTrade.direction === "long"
                    ? "text-green"
                    : "text-red"
                }
              />
              <InfoCard
                title="Entry Price"
                value={`$${parseFloat(coinData.openTrade.entry_price).toFixed(
                  5
                )}`}
              />
              <InfoCard
                title="Entry Time"
                value={formatTimestamp(coinData.openTrade.created_at)}
              />
            </div>
          ) : (
            <p>No active trade for this coin.</p>
          )}
        </div>
        <div className="card">
          <div className="history-header">
            <h2 className="card-title">
              Trade History: {localConfig.coin_pair}
            </h2>{" "}
            <div
              className={`pnl-display ${pnl >= 0 ? "text-green" : "text-red"}`}
            >
              Total P/L: ${pnl.toFixed(2)}
            </div>
          </div>
          <div className="date-selector-container">
            <div className="date-selector">
              <label>From:</label>
              <input
                type="datetime-local"
                name="from"
                value={filters.from}
                onChange={handleFilterChange}
                className="date-input"
              />
            </div>
            <div className="date-selector">
              <label>To:</label>
              <input
                type="datetime-local"
                name="to"
                value={filters.to}
                onChange={handleFilterChange}
                className="date-input"
              />
            </div>
            <div className="date-selector-actions">
              <button onClick={handleApplyFilters} className="btn btn-filter">
                Apply
              </button>
              <button onClick={handleClearFilters} className="btn btn-clear">
                Clear
              </button>
            </div>
          </div>
          <div className="table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Direction</th>
                  <th>Entry Time</th>
                  <th>Exit Time</th>
                  <th>Entry Price</th>
                  <th>Exit Price</th>
                  <th>P/L ($)</th>
                </tr>
              </thead>
              <tbody>
                {history.map((trade) => (
                  <tr key={trade.id}>
                    <td
                      className={
                        trade.direction === "long" ? "text-green" : "text-red"
                      }
                    >
                      {trade.direction.toUpperCase()}
                    </td>
                    <td>{formatTimestamp(trade.created_at)}</td>
                    <td>{formatTimestamp(trade.closed_at)}</td>
                    <td>${parseFloat(trade.entry_price).toFixed(5)}</td>
                    <td>
                      $
                      {trade.exit_price
                        ? parseFloat(trade.exit_price).toFixed(5)
                        : "N/A"}
                    </td>
                    <td className={trade.pnl >= 0 ? "text-green" : "text-red"}>
                      ${parseFloat(trade.pnl || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="controls-column">
        <div className="card">
          <h2 className="card-title">Bot Controls: {localConfig.coin_pair}</h2>
          <div className="controls-grid">
            <button
              onClick={handleStartStop}
              className={`btn ${isBotRunning ? "btn-secondary" : "btn-start"}`}
            >
              {isBotRunning
                ? `Stop ${localConfig.coin_pair} Bot`
                : `Start ${localConfig.coin_pair} Bot`}
            </button>
            <button onClick={handlePanic} className="btn btn-panic">
              PANIC COIN
            </button>
          </div>
          <div className="control-section">
            <h2 className="card-title">
              Risk & Strategy for {localConfig.coin_pair}
            </h2>
            <div className="risk-management-card">
              <div className="risk-input-group">
                <label>
                  <input
                    type="checkbox"
                    checked={!!localConfig.reverse_on_close}
                    disabled={isBotRunning}
                    onChange={(e) =>
                      handleSettingChange("reverse_on_close", e.target.checked)
                    }
                  />
                  Reverse on Signal
                </label>
              </div>
              <div className="trade-size-control">
                <label>
                  Trade Size (
                  {parseFloat(localConfig.position_size_percent || 0).toFixed(
                    1
                  )}
                  %)
                </label>
                <div className="trade-size-slider">
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={localConfig.position_size_percent || 0}
                    disabled={isBotRunning}
                    onChange={(e) =>
                      handleSettingChange(
                        "position_size_percent",
                        e.target.value
                      )
                    }
                  />
                  <input
                    type="text"
                    readOnly
                    className="text-input"
                    style={{ width: "100px" }}
                    value={`$${tradeAmount}`}
                  />
                </div>
              </div>
              <div className="risk-input-group">
                <label>
                  <input
                    type="checkbox"
                    checked={!!localConfig.use_stop_loss}
                    disabled={isBotRunning}
                    onChange={(e) =>
                      handleSettingChange("use_stop_loss", e.target.checked)
                    }
                  />
                  Use Stop-Loss
                </label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={localConfig.stop_loss_percent || 0}
                    onChange={(e) =>
                      handleSettingChange("stop_loss_percent", e.target.value)
                    }
                    disabled={!localConfig.use_stop_loss || isBotRunning}
                    className="text-input"
                  />{" "}
                  %
                </div>
              </div>
              <div className="risk-input-group">
                <label>
                  <input
                    type="checkbox"
                    checked={!!localConfig.use_take_profit}
                    disabled={isBotRunning}
                    onChange={(e) =>
                      handleSettingChange("use_take_profit", e.target.checked)
                    }
                  />
                  Use Take-Profit
                </label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={localConfig.take_profit_percent || 0}
                    onChange={(e) =>
                      handleSettingChange("take_profit_percent", e.target.value)
                    }
                    disabled={!localConfig.use_take_profit || isBotRunning}
                    className="text-input"
                  />{" "}
                  %
                </div>
              </div>
              <button
                onClick={handleSaveChanges}
                className="btn btn-save"
                style={{ marginTop: "1rem" }}
                disabled={isBotRunning}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddCoinModal({ onClose, onCoinAdded, existingCoins }) {
  const [instruments, setInstruments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    apiService.fetchInstruments().then(setInstruments).catch(console.error);
  }, []);

  const handleAdd = async (coinPair) => {
    await apiService.addCoin(coinPair);
    onCoinAdded();
    onClose();
  };

  const filtered = instruments.filter(
    (inst) =>
      inst.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !existingCoins.includes(inst)
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <h2>Add Coin to Dashboard</h2>
        <input
          type="text"
          placeholder="Search for a coin (e.g., ETH/USDT)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="instrument-list">
          {filtered.map((inst) => (
            <div key={inst} className="instrument-item">
              <span>{inst}</span>
              <button onClick={() => handleAdd(inst)} className="btn-add">
                +
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={onClose}
          className="btn btn-secondary"
          style={{ marginTop: "1rem" }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

function TradeSummaryPage() {
  const [summaryData, setSummaryData] = useState({ trades: [], totalPnl: 0 });
  const [filters, setFilters] = useState({ from: "", to: "" });

  useEffect(() => {
    apiService
      .fetchTradeHistory(filters)
      .then(setSummaryData)
      .catch(console.error);
  }, [filters]);

  const formatTimestamp = (ts) => {
    if (!ts) return "N/A";
    const dateObj = new Date(ts);
    if (isNaN(dateObj)) return "Invalid Date";
    return dateObj.toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  return (
    <div className="card">
      <h1 className="card-title">Global Trade Summary</h1>
      <div className="history-header">
        <div className="date-selector">
          <input
            type="date"
            className="date-input"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          />{" "}
          to
          <input
            type="date"
            className="date-input"
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          />
        </div>
        <div
          className={`pnl-display ${
            summaryData.totalPnl >= 0 ? "text-green" : "text-red"
          }`}
        >
          Total P/L: ${summaryData.totalPnl.toFixed(2)}
        </div>
      </div>
      <div className="table-container">
        <table className="history-table">
          <thead>
            <tr>
              <th>Coin</th>
              <th>Direction</th>
              <th>Entry Time</th>
              <th>Exit Time</th>
              <th>P/L ($)</th>
            </tr>
          </thead>
          <tbody>
            {summaryData.trades.map((trade) => (
              <tr key={trade.id}>
                <td>{trade.coin_pair}</td>
                <td
                  className={
                    trade.direction === "long" ? "text-green" : "text-red"
                  }
                >
                  {trade.direction.toUpperCase()}
                </td>
                <td>{formatTimestamp(trade.created_at)}</td>
                <td>{formatTimestamp(trade.closed_at)}</td>
                <td className={trade.pnl >= 0 ? "text-green" : "text-red"}>
                  ${parseFloat(trade.pnl || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
